import axios from "axios";
import {
  assignTicket,
  createCallUrl,
  createNote,
  deleteNote,
  getChatsByStatus,
  getContacts,
  getCustomersAllInbox,
  getCustomersByChat,
  getOptionsCreate,
  getTeams,
  getUsers,
  transcriptionUrl,
} from "@/config/urls.config";

export interface ApiResponse<T> {
  status: boolean;
  data?: T;
  message?: string;
  to?: string;
}

/**
 * Fetch create options for the inbox module
 */
export const fetchOptionsCreate = async (
  token: string,
  supplierUuid: string,
): Promise<ApiResponse<string[]>> => {
  try {
    const response = await axios.get(`${getOptionsCreate}${supplierUuid}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    console.log("response", response);
    const data = response.data.options || [];

    return { status: true, data: data };
  } catch (error) {
    console.error("Error getting options", error);
    return {
      status: false,
      message: "Error getting options",
    };
  }
};

/**
 * Fetch users for a supplier
 */
export const fetchUsers = async (
  token: string,
  supplierUuid: string,
  teamId: string | null = null,
): Promise<
  ApiResponse<Array<{ id: string; name: string; [key: string]: any }>>
> => {
  try {
    const body = {
      supplierUuid: supplierUuid,
      teamId: teamId,
    };
    const response = await axios.post(getUsers, body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = response.data.users || [];

    let sortedData: Array<{ id: string; name: string; [key: string]: any }> =
      [];
    if (data.length > 0) {
      sortedData = data.sort((a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name),
      );
    } else {
      sortedData = [];
    }

    return { status: true, data: sortedData };
  } catch (error) {
    console.error("Error getting users", error);
    return {
      status: false,
      message: "Error getting users",
    };
  }
};

/**
 * Fetch teams for a supplier
 */
export const fetchTeamsSupplier = async (
  token: string,
  supplierUuid: string,
): Promise<
  ApiResponse<Array<{ id: string; name: string; [key: string]: any }>>
> => {
  try {
    const body = {
      supplierUuid: supplierUuid,
    };
    const response = await axios.post(getTeams, body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = response.data.teams;

    const sortedData = data.sort((a: { name: string }, b: { name: string }) =>
      a.name.localeCompare(b.name),
    );

    return { status: true, data: sortedData };
  } catch (error) {
    console.error("Error getting teams", error);
    return {
      status: false,
      message: error.response?.data?.message || "Error getting teams",
    };
  }
};

/**
 * Assign ticket to user or team
 */
export const fetchAssignTicket = async (
  token: string,
  body: {
    userId: string;
    teamId: string;
    ticket: unknown;
    assignedUserId: string;
  },
): Promise<ApiResponse<unknown>> => {
  try {
    const response = await axios.post(assignTicket, body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return { status: true, data: response.data };
  } catch (error) {
    console.error("Error assigning ticket", error);
    return {
      status: false,
      message: "Error assigning ticket",
    };
  }
};

/**
 * Create a call
 */
export const createCall = async (
  token: string,
  body: Record<string, unknown>,
): Promise<ApiResponse<unknown>> => {
  try {
    const response = await axios.post(createCallUrl, body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return {
      status: true,
      data: response.data,
      message: response.data.message,
    };
  } catch (error) {
    console.error("Error creating call", error);
    return {
      status: false,
      message: "Error creating call",
    };
  }
};

/**
 * Send a note/internal message
 */
export const sendNote = async (
  token: string,
  body: Record<string, unknown>,
): Promise<ApiResponse<unknown>> => {
  try {
    const response = await axios.post(createNote, body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return { status: true, data: response.data };
  } catch (error) {
    console.error("Error creating note", error);
    return {
      status: false,
      message: "Error creating note",
    };
  }
};

/**
 * Get transcription for a message
 */
export const getTranscription = async (
  token: string,
  body: Record<string, unknown>,
): Promise<ApiResponse<unknown>> => {
  try {
    const response = await axios.post(transcriptionUrl, body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return {
      status: true,
      data: response.data,
      message: response.data.message,
    };
  } catch (error) {
    console.error("Error getting transcription", error);
    return {
      status: false,
      data: "",
      message: error?.response?.data?.message || "Error getting transcription",
    };
  }
};

/**
 * Delete a note
 */
export const fetchDeleteNote = async (
  token: string,
  body: Record<string, unknown>,
): Promise<ApiResponse<unknown>> => {
  try {
    const response = await axios.post(deleteNote, body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return { status: true, data: response.data };
  } catch (error) {
    console.error("Error deleting note", error);
    return {
      status: false,
      message: error?.response?.data?.message || "Error deleting note",
    };
  }
};

/**
 * Fetch customers for a chat
 */
export const fetchCustomersByChat = async (
  token: string,
  body: Record<string, unknown>,
): Promise<ApiResponse<unknown> & { to?: string }> => {
  try {
    const response = await axios.post(getCustomersByChat, body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = response.data.customers;
    if (!response.data.success) {
      return {
        status: false,
        data: response.data.message,
        to: response.data.to,
      };
    }
    return { status: true, data: data, to: response.data.to };
  } catch (error) {
    console.error("Error getting customers by chat", error);
    return {
      status: false,
      data: error.response?.data?.message || "Error getting customers by chat",
    };
  }
};

/**
 * Fetch contacts by status (open, closed, snoozed)
 */
export const fetchContactsByStatus = async (
  token: string,
  state: string,
  supplierUuid: string,
): Promise<ApiResponse<unknown>> => {
  try {
    let body: Record<string, unknown>;
    if (state === "snoozed") {
      body = {
        status: state,
        supplierUuid: supplierUuid,
      };
      console.log("state", state);
    } else {
      body = {
        status: state,
        supplierUuid: supplierUuid,
        days: 1,
      };
    }
    const response = await axios.post(getChatsByStatus, body, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = response.data.tickets;

    return { status: true, data: data };
  } catch (error) {
    console.error("Error getting contacts by status", error);
    return {
      status: false,
      message:
        error instanceof Error
          ? error.message
          : "Error getting contacts by status",
    };
  }
};

/**
 * Fetch customers in inbox
 */
export const fetchCustomersInbox = async (
  token: string,
  supplierUuid: string,
): Promise<ApiResponse<unknown>> => {
  try {
    const response = await axios.get(`${getCustomersAllInbox}${supplierUuid}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = response.data.customers;
    if (!response.data.success) {
      return {
        status: false,
        data: response.data.message,
      };
    }
    return { status: true, data: data };
  } catch (error) {
    console.error("Error getting customers", error);
    return {
      status: false,
      data: error?.response?.data?.message || "Error getting customers",
    };
  }
};

/**
 * Fetch contacts/chats
 */
export const fetchContacts = async (
  token: string,
  supplierUuid: string,
): Promise<ApiResponse<unknown>> => {
  try {
    const response = await axios.get(`${getContacts}${supplierUuid}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    const data = response.data.contacts;
    if (!response.data.success) {
      return {
        status: false,
        data: response.data.message,
      };
    }
    return { status: true, data: data };
  } catch (error) {
    console.error("Error getting contacts", error);
    return {
      status: false,
      data: error?.response?.data?.message || "Error getting contacts",
    };
  }
};
